import { useEffect, useRef, useState } from 'react';
import { loadLevelSprite } from '../levelSprites';
import './LevelSprite.css';

/** Display pixels per sprite pixel: as big as fits the stage, never past 3×. */
const STAGE_H = 112;
const MAX_ZOOM = 3;

const ENTER_MS = 1100;
const MOVE_FRAME_MS = 120;
const EMOTE_FRAME_MS = 150;
const EMOTE_FRAMES = 6;
const IDLE_FRAME_MS = 520;
const SAY_MS = 1600;

/**
 * A level animal on the level sheet: it walks in from the right when shown, does its
 * emote (signature move + its word in a bubble), then idles. Every tap emotes again.
 *
 * `data-state` (enter | emote | idle) is the component's own record of what it is
 * doing — it changes on the tap itself, before any frame is drawn, so it holds in
 * tests (jsdom has no canvas) and for screen readers alike.
 *
 * While the sprite chunk loads (a few KB), the level's emoji holds the same box, so
 * nothing shifts when the canvas arrives.
 */
export default function LevelSprite({ level, icon, name }) {
  const [sprite, setSprite] = useState(null);
  const [state, setState] = useState('enter');
  const [taps, setTaps] = useState(0);
  const [saying, setSaying] = useState(false);
  const canvasRef = useRef(null);
  const actorRef = useRef(null);

  useEffect(() => {
    let live = true;
    setSprite(null);
    setState('enter');
    loadLevelSprite(level).then((s) => { if (live) setSprite(s); }).catch(() => {});
    return () => { live = false; };
  }, [level]);

  // The animation: one timeline per (sprite, state, tap). Each run cancels the last.
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext?.('2d');
    if (!sprite || !ctx) return undefined;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const paint = (frame) => {
      ctx.clearRect(0, 0, sprite.w, sprite.h);
      frame.forEach((row, y) => {
        for (let x = 0; x < row.length; x++) {
          const c = sprite.palette[row[x]];
          if (c) { ctx.fillStyle = c; ctx.fillRect(x, y, 1, 1); }
        }
      });
    };
    const timers = [];
    let raf = 0;
    const later = (fn, ms) => timers.push(setTimeout(fn, ms));
    const setX = (px) => { if (actorRef.current) actorRef.current.style.transform = `translateX(${px}px)`; };

    const idle = () => {
      let i = 0;
      const step = () => { paint(sprite.idle[i++ % sprite.idle.length]); later(step, IDLE_FRAME_MS); };
      step();
    };
    const emote = () => {
      setSaying(true);
      later(() => setSaying(false), SAY_MS);
      if (reduce) { paint(sprite.action[0]); later(() => { setState('idle'); }, SAY_MS); return; }
      for (let i = 0; i < EMOTE_FRAMES; i++) later(() => paint(sprite.action[i % sprite.action.length]), i * EMOTE_FRAME_MS);
      later(() => setState('idle'), EMOTE_FRAMES * EMOTE_FRAME_MS);
    };

    if (state === 'enter') {
      if (reduce) { setX(0); later(() => setState('emote'), 0); }
      else {
        const from = (actorRef.current?.parentElement?.clientWidth || 300) / 2 + sprite.w * MAX_ZOOM;
        const t0 = performance.now();
        const tick = (now) => {
          const ms = Math.max(0, now - t0);
          const k = Math.min(1, ms / ENTER_MS);
          setX(from * (1 - k) * (1 - k));
          paint(sprite.move[Math.floor(ms / MOVE_FRAME_MS) % sprite.move.length]);
          if (k < 1) raf = requestAnimationFrame(tick);
          else setState('emote');
        };
        raf = requestAnimationFrame(tick);
      }
    } else if (state === 'emote') {
      setX(0);
      emote();
    } else {
      setX(0);
      idle();
    }
    return () => { cancelAnimationFrame(raf); timers.forEach(clearTimeout); };
  }, [sprite, state, taps]);

  const zoom = sprite ? Math.min(MAX_ZOOM, STAGE_H / sprite.h) : 1;
  const onTap = () => {
    setState('emote');
    setTaps((n) => n + 1);
  };

  return (
    <button
      type="button"
      className="level-sprite"
      data-testid="level-sprite"
      data-level={level}
      data-state={state}
      aria-label={`${name}，点一下`}
      onClick={onTap}
    >
      <span className="level-sprite-actor" ref={actorRef}>
        {sprite ? (
          <canvas
            ref={canvasRef}
            width={sprite.w}
            height={sprite.h}
            style={{ width: sprite.w * zoom, height: sprite.h * zoom }}
          />
        ) : (
          <span className="level-sprite-fallback" aria-hidden="true">{icon}</span>
        )}
      </span>
      {sprite && saying && <span className="level-sprite-say">{sprite.say}</span>}
    </button>
  );
}
